import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { FixtureViewType } from '@cds/betting-offer';
import { FixtureForm } from '@cds/betting-offer/add-ons';
import { CouponPage } from '@cds/betting-offer/domain-specific/coupons';
import { EventModel, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { OfferGroupingService } from '@frontend/sports/betting-offer/feature/offer-grouping';
import { MultiBuilderConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { PropType } from '@frontend/sports/common/core/utils/extended-types';
import { HooksWireup, OnRouteResolve } from '@frontend/sports/common/core/utils/hooks';
import { EventSubscription } from '@frontend/sports/event-subscription/feature/base-subscription';
import { GridSubscriptionService } from '@frontend/sports/event-subscription/feature/grid-subscription';
import { EventFactory } from '@frontend/sports/grid/core/feature/factories';
import { GridEvent } from '@frontend/sports/grid/core/feature/model';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { LocalStoreService } from '@frontend/vanilla/core';
import { clone } from 'lodash-es';
import { NgxPopperjsContentComponent } from 'ngx-popperjs';

import { OptionGroupService } from '../event-model/helpers/option-group.service';
import { NavigationRouterService } from '../navigation/navigation-router.service';
import { MultiBuilderTrackingService } from './multi-builder-tracking.service';
import { EventsByDay, MarketGroupItem, MultiBuilderConstants, MultiBuilderDispatcherEvents, MultibuilderResponse } from './multi-builder.model';
import { MultiBuilderService } from './multi-builder.service';

@HooksWireup()
@Component({
    selector: 'ms-multi-builder',
    templateUrl: 'multi-builder.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class MultiBuilderComponent extends OnRouteResolve<MultibuilderResponse> implements OnInit, OnDestroy {
    @HostBinding('class') className = 'multi-builder';
    @ViewChild('onBoardingContent') onBoardingPopper: NgxPopperjsContentComponent;

    private marketGroupState = new Map<string, { leagueId: string; marketId: string }>();
    marketGroups: MarketGroupItem[] = [];
    selectedTab: MarketGroupItem;
    multiBuilderEvents: EventsByDay[] = [];
    betslipErrorMessages: PropType<PropType<Sitecore, 'betslip'>, 'errors'>;
    optionNames: string[] = [];
    private eventSubscriptions?: EventSubscription<EventModel>;
    showOnboardingMessage = false;
    eventUrl: string;
    optionGroups: { [key: string]: EventOptionGroup } = {};
    statisticsMapping: { [key: string]: FixtureForm | undefined } = {};
    currentCoupon: string;
    isDataRefresh: boolean;

    constructor(
        public sitecore: Sitecore,
        private eventFactory: EventFactory,
        route: ActivatedRoute,
        private navigationRouter: NavigationRouterService,
        private multiBuilderService: MultiBuilderService,
        private redirectHelper: RedirectHelperService,
        private gridSubscription: GridSubscriptionService,
        private localStorage: LocalStoreService,
        private offerGroupingService: OfferGroupingService,
        private changeDetectorRef: ChangeDetectorRef,
        private dispatcherService: DispatcherService,
        private multiBuilderTracking: MultiBuilderTrackingService,
        private optionGroupService: OptionGroupService,
        private multiBuilderConfig: MultiBuilderConfig,
    ) {
        super(route);
        this.betslipErrorMessages = sitecore.betslip.errors;
    }

    onRouteResolved(model: MultibuilderResponse): void {
        if (!model?.subNavigationItems.length) {
            this.redirectHelper.goHome(undefined, true);

            return;
        }
        this.setModel(model.fixtures);
        this.dispatcherService.dispatch(MultiBuilderDispatcherEvents.OnMultiBuilderPage, true);
    }

    ngOnInit(): void {
        this.navigationRouter.setNavigation('multibuilder');

        if (!this.localStorage.get(MultiBuilderConstants.OnBoardingMessageKey)) {
            this.showOnboardingMessage = true;
            this.localStorage.set(MultiBuilderConstants.OnBoardingMessageKey, true);
        }
    }

    ngOnDestroy(): void {
        this.dispatcherService.dispatch(MultiBuilderDispatcherEvents.OnMultiBuilderPage, false);
        this.unsubscribe();
    }

    private displayDay(events: GridEvent[]): boolean {
        if (!this.selectedTab) {
            return false;
        }

        return events.some((event) => event.optionGroups.some((o) => o.gridGroups?.includes(this.selectedTab.id)));
    }

    showDate(multiBuilderEvent: EventsByDay): boolean {
        return multiBuilderEvent.day !== this.sitecore.globalMessages.NavLive && this.displayDay(multiBuilderEvent.fixtures);
    }

    onSelectedTab = (group: MarketGroupItem | undefined, track?: boolean): void => {
        if (!group) {
            return;
        }

        this.selectedTab = group;
        this.selectedTab.active = true;
        if (this.selectedTab.showColumnName) {
            this.getColumnNames(this.selectedTab.id, this.selectedTab.sportId);
        }
        this.setOptionGroups();
        this.checkIfDataRefresh();
        this.marketGroupState.set(MultiBuilderConstants.SelectedMarketGroupKey, {
            leagueId: this.currentCoupon,
            marketId: this.selectedTab.id,
        });
        if (!this.isDataRefresh && track) {
            this.multiBuilderTracking.trackMarKetSwitch(this.selectedTab.id.toString());
        }
    };

    private setOptionGroups(): void {
        this.optionGroups = {};
        this.multiBuilderEvents.forEach((event) =>
            event.fixtures.forEach((fixture) => {
                const groups = this.getOptionGroup(fixture);
                if (groups) {
                    this.optionGroups[fixture.id] = groups;
                }
            }),
        );
    }

    private getOptionGroup(gridEvent: GridEvent): EventOptionGroup | undefined {
        const optionGroup = this.optionGroupService.get(gridEvent.optionGroups, this.selectedTab.id);
        if (this.selectedTab.showColumnName) {
            optionGroup?.options.map((option, index) => {
                option.overridenName = this.optionNames[index];
            });
        }

        return optionGroup;
    }

    private getColumnNames(groupId: string, sportId?: number): void {
        if (!sportId) {
            return;
        }
        this.offerGroupingService.getGridGrouping(sportId).subscribe((gridGroupConfig) => {
            this.optionNames = gridGroupConfig?.groups.find((group) => group.id === groupId)?.optionNames || [];
            this.changeDetectorRef.markForCheck();
        });
    }

    private setModel(couponPage: CouponPage | undefined): void {
        this.statisticsMapping = {};
        if (couponPage?.fixturePage.fixtures) {
            this.currentCoupon = couponPage.coupon.id.toString();
            this.eventFactory.create(couponPage.fixturePage.fixtures).subscribe((fixtures) => {
                this.buildMultibuilderEvents(fixtures);

                if (this.multiBuilderConfig.showStats) {
                    fixtures.forEach((fixture) => {
                        this.statisticsMapping[fixture.id] = this.mapStatistics(fixture);
                    });
                }

                this.changeDetectorRef.markForCheck();
            });
        }
    }

    private mapStatistics(fixture: GridEvent): FixtureForm | undefined {
        if (fixture.viewType === FixtureViewType.American) {
            return {
                overallAwayTeamForm: fixture.fixtureForm?.overallHomeTeamForm,
                overallHomeTeamForm: fixture.fixtureForm?.overallAwayTeamForm,
                awayTeamAwayForm: fixture.fixtureForm?.homeTeamHomeForm,
                homeTeamHomeForm: fixture.fixtureForm?.awayTeamAwayForm,
            };
        }

        return fixture.fixtureForm;
    }

    private buildMultibuilderEvents(fixtures: GridEvent[]): void {
        this.marketGroups = [];
        this.multiBuilderEvents = [];
        if (fixtures.length) {
            this.multiBuilderEvents = this.multiBuilderService.groupEvents(fixtures);
            this.marketGroups = this.multiBuilderService.getMarketGridGroups(fixtures);
            const previousSelection = this.marketGroupState.get(MultiBuilderConstants.SelectedMarketGroupKey);
            if (previousSelection?.marketId && previousSelection.leagueId && this.marketGroups.length) {
                const marketGroup = this.marketGroups.find(
                    (group) => group.id === previousSelection.marketId && previousSelection.leagueId === this.currentCoupon.toString(),
                );
                if (marketGroup) {
                    this.onSelectedTab(marketGroup);
                } else {
                    this.onSelectedTab(this.marketGroups[0]);
                }
            } else if (this.marketGroups.length) {
                this.onSelectedTab(this.marketGroups[0]);
            }
            this.subscribe();
            if (!this.isDataRefresh) {
                this.multiBuilderTracking.trackCompetitionSwitch(fixtures[0].sport.id);
            }
        }
        this.changeDetectorRef.markForCheck();
    }

    private checkIfDataRefresh(): void {
        const previousSelection = this.marketGroupState.get(MultiBuilderConstants.SelectedMarketGroupKey);
        this.isDataRefresh = !!(
            !previousSelection ||
            (previousSelection && previousSelection.marketId === this.selectedTab.id && this.currentCoupon === previousSelection.leagueId)
        );
    }

    private subscribe(): void {
        this.unsubscribe();

        if (!this.multiBuilderEvents.length) {
            return;
        }

        const callbacks = this.multiBuilderEvents.flatMap((multibuilderEvent, eventIndex) =>
            multibuilderEvent.fixtures.map((model, fixtureIndex) => ({
                event: clone(model),
                callback: this.handler(eventIndex, fixtureIndex),
            })),
        );

        if (callbacks.length > 0) {
            this.eventSubscriptions = this.gridSubscription.subscribe(callbacks);
        }
    }

    private unsubscribe(): void {
        this.eventSubscriptions?.unsubscribe();
    }

    trackFixtures(index: any, event: GridEvent): string {
        return event.id;
    }

    private handler(eventIndex: number, fixtureIndex: number): (updatedEvent: EventModel) => void {
        return (updatedEvent) => {
            if (!updatedEvent) {
                return;
            }

            this.multiBuilderEvents[eventIndex].fixtures[fixtureIndex] = clone(updatedEvent);
            const groups = this.getOptionGroup(updatedEvent);

            if (groups) {
                this.optionGroups[updatedEvent.id] = groups;
            }

            this.changeDetectorRef.markForCheck();
        };
    }
}
